import React, { useEffect, useState } from 'react';
import { CalendarDays, Heart, ShieldCheck } from 'lucide-react';
import { happyCustomers, ratings } from '../mock';
import { GoogleLogo, TripAdvisorLogo } from './Rating';

const INTERVAL = 5000;
const YEARS = new Date().getFullYear() - 1995;

const Seg = ({ icon: Icon, children, short, testId }) => (
  <div className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap" data-testid={testId}>
    {Icon && <span className="shrink-0 [&>svg]:w-4 [&>svg]:h-4 sm:[&>svg]:w-[18px] sm:[&>svg]:h-[18px] [&>img]:w-4 [&>img]:h-4 sm:[&>img]:w-[18px] sm:[&>img]:h-[18px] text-primary flex"><Icon size={18} strokeWidth={1.75} /></span>}
    <span className="t-label-lg text-xs sm:text-sm text-onsurface">
      {short ? <><span className="sm:hidden">{short}</span><span className="hidden sm:inline">{children}</span></> : children}
    </span>
  </div>
);
const Sep = () => <span className="block h-4 sm:h-5 w-px bg-outline-variant shrink-0" />;
const Group = ({ children }) => <div className="flex items-center gap-x-2 sm:gap-6 flex-nowrap justify-center">{children}</div>;
const B = ({ children }) => <span className="font-semibold">{children}</span>;

const HAPPY_SLIDE = {
    key: 'happy',
    node: (
      <Group>
        <div className="flex items-center gap-2.5 whitespace-nowrap" data-testid="trust-slide-happy">
          <span className="flex -space-x-2">
            {happyCustomers.avatars.slice(0, 3).map((src) => (
              <span key={src} className="relative w-7 h-7 rounded-full ring-2 ring-secondary-container overflow-hidden bg-surface-container"><img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" /></span>
            ))}
          </span>
          <span className="t-label-lg text-xs sm:text-sm text-onsurface">{happyCustomers.text}</span>
          <Heart size={18} className="heart-beat text-[#FF3B4A] fill-[#FF3B4A] drop-shadow-[0_2px_6px_rgba(255,59,74,0.45)]" aria-hidden="true" />
        </div>
      </Group>
    )
  };

const SLIDES = [
  {
    key: 'ratings',
    node: (
      <Group>
        <Seg icon={GoogleLogo} testId="rating-google">Rated <B>{ratings.google.score}</B> on Google</Seg>
        <Sep />
        <Seg icon={TripAdvisorLogo} testId="rating-tripadvisor">Rated <B>{ratings.tripadvisor.score}</B> on TripAdvisor</Seg>
      </Group>
    )
  },
  {
    key: 'heritage',
    node: (
      <Group>
        <Seg icon={CalendarDays} testId="trust-slide-heritage">Established in <B>1995</B></Seg>
        <Sep />
        <Seg testId="trust-slide-years"><span className="hidden sm:inline">Over </span><B>{YEARS}</B> years of trust</Seg>
      </Group>
    )
  },
  {
    key: 'iso',
    node: (
      <Group>
        <Seg icon={ShieldCheck} testId="trust-slide-iso" short={<><B>ISO 45001</B> certified</>}>Hi Tours is <B>ISO 45001</B> certified</Seg>
        <Sep />
        <Seg testId="trust-health-safety">Health <span className="sm:hidden">&amp;</span><span className="hidden sm:inline">and</span> Safety is our priority</Seg>
      </Group>
    )
  }
];

const useWide = () => {
  const [wide, setWide] = useState(() => window.matchMedia('(min-width: 400px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 400px)');
    const fn = (e) => setWide(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return wide;
};

export default function TrustBar({ happy = false }) {
  const slides = happy ? [...SLIDES, HAPPY_SLIDE] : SLIDES;
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const wide = useWide();

  useEffect(() => {
    if (paused || !wide) return undefined;
    const t = setTimeout(() => setI((v) => (v + 1) % slides.length), INTERVAL);
    return () => clearTimeout(t);
  }, [paused, i, wide, slides.length]);

  const next = () => { if (wide) setI((v) => (v + 1) % slides.length); };
  const slide = wide ? slides[i] : slides.find((s) => s.key === 'ratings');
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={next}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); next(); } }}
      className="bg-secondary-container h-11 flex items-center justify-center px-3 sm:px-4 overflow-hidden select-none cursor-pointer active:bg-secondary-dim transition-colors duration-200 focus-visible:outline-none"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="trust-bar"
      data-slide={slide.key}
      aria-live="polite"
    >
      <div key={slide.key} className="w-full flex justify-center animate-[hi-slide-up_500ms_cubic-bezier(.22,.61,.36,1)]" data-testid="trust-bar-slide">
        {slide.node}
      </div>
    </div>
  );
}
